// providerLogos.js — the data providers, and their marks.
//
// A number on a page belongs to StatsBomb, SkillCorner or IMPECT, and which one it is changes what
// the number means: the same "pressures" from two feeds are two different definitions. That
// attribution used to be a two-letter tag — SB / SC / IM — which is only legible to someone who
// already knows the answer. The providers' own marks are recognised instantly, so they carry it.
//
// The artwork comes from shapes/data providers/ (see scripts/copy_logos.py), and the filename is
// read from that folder's manifest rather than assumed, so a mark resupplied as an .svg keeps
// working. Until the manifest arrives — and if a mark is ever missing — the short tag stands in,
// which is why it survives here.
import React, { useState } from 'react';

import { DATA_PROVIDERS, artworkUrl, useManifest } from './artwork';

// Every feed we hold a mark for. The Metric Explorer only reads three of them; the rest are here
// because the marks are published and used — the sidebar names each provider page with its own
// mark rather than a drawn glyph.
export const PROVIDER_LABEL = {
  statsbomb: 'StatsBomb', skillcorner: 'SkillCorner', impect: 'IMPECT', opta: 'Opta',
  catapult: 'Catapult', firstbeat: 'Firstbeat', orreco: 'Orreco',
};
export const PROVIDER_SHORT = {
  statsbomb: 'SB', skillcorner: 'SC', impect: 'IM', opta: 'OP',
  catapult: 'CP', firstbeat: 'FB', orreco: 'OR',
};

/** public/data-providers/manifest.json, once per page. */
export function useProviderManifest() {
  return useManifest(DATA_PROVIDERS);
}

/**
 * One provider's mark. `manifest` is what useProviderManifest() returned; pass it down rather
 * than calling the hook here, so a list of fifty tags is one fetch and not fifty subscriptions.
 * The caller owns the sizing — this renders an <img> and nothing else.
 *
 * `fallback` is what stands in while the manifest is in flight, or if the mark is missing: the
 * short tag where the mark is the only attribution there is, and nothing where the provider is
 * already named in words beside it.
 */
export function ProviderLogo({ source, manifest, className = '', title, fallback = 'short' }) {
  const [failed, setFailed] = useState(false);
  const label = PROVIDER_LABEL[source] || source;
  const file = manifest && manifest[source];
  if (!file || failed) {
    if (fallback === 'none') return null;
    return <span className={className} title={title || label}>{PROVIDER_SHORT[source] || '—'}</span>;
  }
  return (
    <img className={className} src={artworkUrl(DATA_PROVIDERS, file)} alt={label}
      title={title || label} draggable="false" onError={() => setFailed(true)} />
  );
}

/**
 * One provider's mark, fetching the manifest itself.
 *
 * The difference from ProviderLogo is who owns the manifest. A list of fifty tags passes one down
 * so it is one subscription; a handful of marks scattered across a tree — the sidebar's provider
 * pages — would have to thread it through every level to do that, and loadManifest already shares
 * a single request between every caller, so here each mark just asks.
 *
 * `fallback` is what renders until the manifest arrives, and if the mark turns out to be missing.
 * Passing the glyph the page used to carry means the sidebar never shows a gap.
 */
export function ProviderMark({ source, className = '', title, fallback = null }) {
  const manifest = useProviderManifest();
  const [failed, setFailed] = useState(false);
  const file = manifest && manifest[source];
  if (!file || failed) return fallback;
  return (
    <img className={className} src={artworkUrl(DATA_PROVIDERS, file)}
      alt={PROVIDER_LABEL[source] || source} title={title} draggable="false"
      onError={() => setFailed(true)} />
  );
}

export default ProviderLogo;
